interface HeroSectionProps {
  onRegisterClick: () => void;
}

export default function HeroSection({ onRegisterClick }: HeroSectionProps) {
  const badges = [
    { icon: '🗓', text: '16–17 апреля 2026' },
    { icon: '📍', text: 'Гостиница «Москва», СПб' },
    { icon: '🎤', text: 'Стендап между секциями' },
    { icon: '🤖', text: 'AI-консультант' },
    { icon: '🚶', text: 'Прогулка по центру' },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background collage */}
      <div className="absolute inset-0 flex">
        <div className="w-[40%] relative overflow-hidden">
          <img
            src="/img/hero-conference.jpg"
            alt="Конференц-зал"
            className="w-full h-full object-cover object-top"
            style={{ filter: 'brightness(0.85) saturate(0.8) hue-rotate(10deg)' }}
          />
          <div className="absolute inset-0 bg-[#0f3d32]/60"></div>
          <div className="absolute inset-0 bg-[#1a6b5a]/15 mix-blend-multiply"></div>
        </div>
        <div className="w-[35%] relative overflow-hidden">
          <img
            src="/img/hero-standup.jpg"
            alt="Стендап"
            className="w-full h-full object-cover object-top"
            style={{ filter: 'brightness(0.9) saturate(0.85) hue-rotate(5deg)' }}
          />
          <div className="absolute inset-0 bg-[#0f3d32]/50"></div>
          <div className="absolute inset-0 bg-[#1a6b5a]/15 mix-blend-multiply"></div>
        </div>
        <div className="w-[25%] relative overflow-hidden">
          <img
            src="/img/hero-view.png"
            alt="Вид с Невы на гостиницу Москва и Александро-Невскую лавру"
            className="w-full h-full object-cover object-top"
            style={{ filter: 'brightness(0.88) saturate(0.8) hue-rotate(8deg)' }}
          />
          <div className="absolute inset-0 bg-[#0f3d32]/45"></div>
          <div className="absolute inset-0 bg-[#1a6b5a]/15 mix-blend-multiply"></div>
        </div>
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f3d32]/45 via-[#0f3d32]/30 to-[#0f3d32]/65"></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-32 md:py-40">
        <div className="max-w-3xl">
          {/* Supertitle */}
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
            <span className="text-white/90 text-sm font-medium">
              Всероссийская медицинская конференция • 16–17 апреля 2026 • Санкт-Петербург
            </span>
          </div>

          {/* H1 */}
          <h1 className="text-4xl md:text-7xl font-extrabold text-white leading-tight mb-4">
            NeuroRussia 2026
            <br />
            <span className="text-[#c8963e] text-3xl md:text-7xl">Междисциплинарные</span>
            <br />
            диалоги
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-white/90 font-light leading-relaxed mb-8 max-w-2xl">
            Когда медицина встречается с юмором, а клинические разборы — с медицинским стендапом в самом сердце Петербурга
          </p>

          {/* Lead */}
          <div className="space-y-3 mb-10 max-w-2xl">
            <p className="text-white/85 text-base md:text-lg leading-relaxed">
              Два дня междисциплинарного диалога: между научными секциями — медицинский стендап «Смехосоматика», в обед — AI-консультант, после — прогулка по Невскому и Лавре.
            </p>
            <p className="text-white/85 text-base md:text-lg leading-relaxed">
              &nbsp;Приходите с друзьями — будет полезно и весело.
            </p>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-10">
            {badges.map((badge) => (
              <span
                key={badge.text}
                className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-white text-sm font-medium"
              >
                <span>{badge.icon}</span>
                <span>{badge.text}</span>
              </span>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={onRegisterClick}
              className="bg-[#e8a030] text-white font-extrabold text-base px-10 py-4 rounded-full hover:bg-[#f0aa35] transition-all hover:-translate-y-1 hover:scale-105 whitespace-nowrap cursor-pointer"
              style={{ boxShadow: '0 4px 24px 0 rgba(232,160,48,0.45)' }}
            >
              Регистрация бесплатно
            </button>
            <a
              href="/program16.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold text-base px-8 py-4 rounded-full hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line"></i>
              Программа 16.04
            </a>
            <a
              href="/program17.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white font-semibold text-base px-8 py-4 rounded-full hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line"></i>
              Программа 17.04
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 animate-bounce">
        <i className="ri-arrow-down-line text-2xl"></i>
      </div>
    </section>
  );
}
